import { Button, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { fetchSubscription, updateSubscriptionInstallments, updateSubscriptionAnticipation, cancelSubscription, reactivateSubscription } from '../apis/subscriptionsApi';
import { openNotification } from './notifications';
import utc from 'dayjs/plugin/utc';
import LAST_PAYMENT_STATUS from '../enums/LastPaymentStatus';
import Features from '../enums/features';
import { checkAutoPermissions } from '../apis/featureApi';
import { Link } from 'react-router-dom';

dayjs.extend(utc);

const { Option } = Select;

const Subscription = ({ userId }) => {
  const { authenticatedUser } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [savings, setSavings] = useState(null);
  const [allowUpdate, setAllowUpdate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCancelSubcriptionEnabled, setIsCancelSubcriptionEnabled] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const loadSubscription = async () => {
      try{
        setLoading(true);
        const response = await fetchSubscription(userId);
        if(response.status === 200){
          const { subscription, savings } = response.data;
          setSubscription(subscription);
          setSavings(savings);
        }else{
          openNotification('error', 'Error al cargar la subscripción, intenta más tarde');
          setSubscription(null);
          setSavings(null);
        }
      } catch (error) {
        console.error('Subscription - loadSubscription, Error fetching subscription:', error);
        openNotification('error', 'Error al cargar la subscripción, contacta a soporte');
        setSubscription(null);
        setSavings(null);
      } finally {
        setLoading(false);
      }
    };
    loadSubscription();
  }, [userId, authenticatedUser]);

  useEffect(() => {
    const checkFeatures = async () => {
      setLoading(true);
      const isAuthorized = await checkAutoPermissions(Features.SEE_OTHERS_SUBSCRIPTION, userId);
      if (!isAuthorized) {
          const features = JSON.parse(localStorage.getItem('user_features') || '{}');
          setIsAuthorized(features[Features.SEE_OTHERS_SUBSCRIPTION] || false);
      }else{
          setIsAuthorized(true);
      }
      try{
        const features = JSON.parse(localStorage.getItem('user_features') || '{}');
        setIsCancelSubcriptionEnabled(features[Features.CANCEL_SUBSCRIPTION] || false);
      } catch (err) {
        openNotification('error', 'Error al verificar permisos para la subscripción, contacta a soporte');
      } finally {
        setLoading(false);
      }
    }
    checkFeatures();
  }, [userId]);

  useEffect(() => {
    setAllowUpdate(String(userId) === String(authenticatedUser));
  }, [userId, authenticatedUser]);

  if (!isAuthorized) {
    return null
  }

  const onUpdateInstallments = async (subscriptionId, value) => {
    const result = await updateSubscriptionInstallments(subscriptionId, value);
    if( !result.success ) {
        openNotification(
            "error",
            "Error actualizando las cuotas, intenta más tarde"
        );
        return;
    }
    setSubscription(prev => ({ ...prev, installments: value }));
    openNotification('success', 'Cuotas actualizadas con exito!');
  }

  const onUpdateAnticipation = async (subscriptionId, value) => {
    const result = await updateSubscriptionAnticipation(subscriptionId, value);
    if( !result.success ) {
        openNotification(
            "error",
            "Error actualizando la anticipación, intenta más tarde"
        );
        return;
    }
    setSubscription(prev => ({ ...prev, days_advance_payment: value }));
    openNotification('success', 'Anticipación actualizada con exito!');
  }

  const handleReactivateSubscription = async (subscriptionId) => {
    try {
      await reactivateSubscription(subscriptionId)
      openNotification(
        "success",
        "Subscripción reactivada con éxito."
      );
      // TODO: with connection - refresh component
    } catch (error) {
      console.error('Subscriptions - reactivateSubscription, Error reactivating subscription:', error);
      openNotification(
        "error",
        "Error reactivando la subscripción. Contacta a soporte"
      );
      return;
    }
  }

  const handleCancelSubscription = async (subscriptionId) => {
    // TODO: validate response code if there will be a next payment and update notification message
    try {
      await cancelSubscription(subscriptionId)
      openNotification(
        "success",
        "Se realizará un último cobro para la cancelación. Se envió notificación al usuario. Actualice el PQR"
      );
      // TODO: with connection - refresh component
    } catch (error) {
      console.error('Subscriptions - cancelSubscription, Error canceling subscription:', error);
      openNotification(
        "error",
        "Error cancelando la subscripción. Contacta a soporte"
      );
      return;
    }
  }

  if (loading) {
    return (
      <div className="text-center mt-3">
        <Spin size="large" />
      </div>
    );
  }

  if (!subscription && !savings) return null;

  return (
    <div className="py-3 col-11">
      <h3 className="mb-3">Subscripción:</h3>
      <div className="themed-block text-center text-md-left d-md-flex p-3 mx-auto mb-3">
        <div className="d-flex flex-wrap justify-content-between w-100">
          {!subscription ? (

            <div className="col-12 d-flex flex-column text-center m-auto">
              { savings.savings > 0 &&  (
                <>
                <p className="font-weight-bold mb-1">Podrías haber ahorrado</p>
                <p>
                  {(savings.savings / 100).toLocaleString('es-CL', {
                  style: 'currency',
                  currency: 'CLP',
                  minimumFractionDigits: 0
                  })}
                </p>
                </>
              )}
              <Link to="/plans">
                <Button className='btn mt-2' type="primary">
                    Ver Subscripciones
                </Button>
            </Link>
            </div>
          ) :
          (
            <div className="row">
              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Plan</p>
                <span>{subscription.plan_name}</span>
              </div>

              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Valor</p>
                <span>
                  {(subscription.amount_in_cents != null
                  ? (subscription.amount_in_cents / 100).toLocaleString('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                    })
                  : ''
                  )}
                </span>
              </div>

              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Cuotas</p>
                <Select
                  value={subscription.installments || undefined}
                  style={{ minWidth: 80, margin: '0 auto' }}
                  onChange={val => onUpdateInstallments(subscription.id, val)}
                  disabled={!allowUpdate}
                >
                  {[...Array(12)].map((_, i) => (
                  <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                  ))}
                </Select>
              </div>

              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Anticipación <small>(días)</small></p>
                <Select
                  value={subscription.days_advance_payment || undefined}
                  style={{ minWidth: 80, margin: '0 auto' }}
                  onChange={val => onUpdateAnticipation(subscription.id, val)}
                  disabled={!allowUpdate}
                >
                  {[...Array(7)].map((_, i) => (
                  <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                  ))}
                </Select>
              </div>

              { savings.savings > 0 &&  (
                <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                  <p className="font-weight-bold mb-1">Has ahorrado</p>
                  <p>
                    {(savings.savings / 100).toLocaleString('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                    })}
                  </p>
                </div>
              )}

              {isCancelSubcriptionEnabled && !subscription.last_payment_status && (
                <div className="col-12 d-flex justify-content-center mt-3">
                  <button
                  className="btn btn-danger"
                  onClick={() => handleCancelSubscription(subscription.id)}
                  >
                  Cancelar
                  </button>
                </div>
              )}

              {subscription.last_payment_status && subscription.last_payment_status !== LAST_PAYMENT_STATUS.DONE && (
                <div className="col-12 d-flex flex-column text-center mt-3">
                  <p className="font-weight-bold mb-1">Cancelación</p>
                  <span>Pendiente último cobro</span>
                  <button
                  className="btn btn-secondary mt-2"
                  onClick={() => handleReactivateSubscription(subscription.id)}
                  >
                  Deshacer cancelación
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscription;